import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Play, Trash2, Radar, Plus, ExternalLink,
  TrendingUp, AlertTriangle, Info, Zap, RotateCw, FileText, Gauge, KeyRound,
  BarChart3, ChevronDown,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, LabelList, Cell,
} from "recharts";

function scoreColor(score) {
  if (score == null) return "#94a3b8";
  if (score >= 70) return "#0f9d58";
  if (score >= 50) return "#f97316";
  return "#db4437";
}

function scoreLabel(score) {
  if (score == null) return "—";
  if (score >= 70) return "Good";
  if (score >= 50) return "Needs Improvement";
  return "Poor";
}

function statusColor(status) {
  const s = (status || "").toLowerCase();
  if (["error", "fail", "failed"].includes(s)) return "bg-red-100 text-red-800";
  if (["warning", "warn"].includes(s)) return "bg-amber-100 text-amber-800";
  return "bg-zinc-100 text-zinc-700";
}

/**
 * Client Woorank tab — SEO Review fetcher + history + trend chart.
 *
 * URLs merge the two same sources as the PSI tab (business_units +
 * custom). "Fetch latest" hits Woorank GET /reviews and stores the
 * score. "Refresh crawl" fires POST /reviews to force a new crawl —
 * async, typically completes in <60s.
 */
export default function ClientWoorankPage() {
  const { clientId } = useParams();
  const [urls, setUrls] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  const [refreshing, setRefreshing] = useState(null);
  const [selectedUrl, setSelectedUrl] = useState(null);
  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [adding, setAdding] = useState(false);
  const [masterState, setMasterState] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: urlData }, { data: testData }] = await Promise.all([
        api.get(`/woorank/clients/${clientId}/urls`),
        api.get(`/woorank/clients/${clientId}/tests`),
      ]);
      const urlList = urlData.urls || [];
      setUrls(urlList);
      setTests(testData.tests || []);
      setSelectedUrl((prev) => {
        if (prev && urlList.some((u) => u.url === prev)) return prev;
        return urlList[0]?.url || null;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Woorank data");
    } finally { setLoading(false); }
  }, [clientId]);

  const loadMaster = useCallback(async () => {
    try {
      const { data } = await api.get("/woorank/master");
      setMasterState(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadAll(); loadMaster(); }, [loadAll, loadMaster]);

  const addUrl = async () => {
    if (!newUrl.trim()) { toast.error("Enter a URL"); return; }
    setAdding(true);
    try {
      const { data } = await api.post(`/woorank/clients/${clientId}/urls`, {
        url: newUrl.trim(),
        label: newLabel.trim() || null,
      });
      setUrls(data.urls || []);
      setNewUrl(""); setNewLabel("");
      toast.success("URL added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add URL");
    } finally { setAdding(false); }
  };

  const removeUrl = async (u) => {
    if (u.source === "business_unit") {
      toast.info("Business-unit URLs are managed on the Properties tab.");
      return;
    }
    if (!window.confirm(`Remove ${u.url} from the Woorank list? Existing history stays.`)) return;
    try {
      const { data } = await api.delete(
        `/woorank/clients/${clientId}/urls/${u.url_id}`);
      setUrls(data.urls || []);
      if (selectedUrl === u.url) {
        setSelectedUrl(data.urls?.[0]?.url || null);
      }
      toast.success("URL removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const fetchLatest = async (u) => {
    setRunning(u.url);
    try {
      const { data } = await api.post(`/woorank/clients/${clientId}/run`, {
        url: u.url,
      });
      toast.success(`Fetched Woorank review · score ${data.test?.score ?? "—"}`);
      const { data: testData } = await api.get(
        `/woorank/clients/${clientId}/tests`);
      setTests(testData.tests || []);
      setSelectedUrl(u.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Woorank fetch failed");
    } finally { setRunning(null); }
  };

  const refreshCrawl = async (u) => {
    setRefreshing(u.url);
    // Woorank POST /reviews kicks off a crawl that runs 30-60s
    // server-side and completes via webhook. The initial POST returns
    // an immediate ack. We fire a polling loop that hits Fetch-latest
    // every 15s for up to 2 minutes to persist the new score as soon
    // as the crawl finishes — no more "click Refresh, wait, click
    // Fetch, wait" dance.
    const initialTestIds = new Set(
      tests.filter((t) => t.url === u.url).map((t) => t.test_id));
    let stopped = false;
    const poll = async () => {
      for (let i = 0; i < 8 && !stopped; i++) {
        await new Promise((r) => setTimeout(r, 15000));
        if (stopped) return;
        try {
          // A silent Fetch-latest — writes a new woorank_tests row
          // only if Woorank has fresh data.
          const { data } = await api.post(
            `/woorank/clients/${clientId}/run`, { url: u.url });
          if (data?.test?.test_id && !initialTestIds.has(data.test.test_id)) {
            toast.success(
              `Fresh Woorank crawl completed · score ${data.test.score ?? "—"}`);
            const { data: testData } = await api.get(
              `/woorank/clients/${clientId}/tests`);
            setTests(testData.tests || []);
            return;
          }
        } catch { /* silent — Woorank may still be crawling */ }
      }
    };
    const pollP = poll();
    try {
      const { data } = await api.post(`/woorank/clients/${clientId}/refresh`, {
        url: u.url,
      });
      toast.info(data.detail || "Crawl requested · auto-polling for the new score…");
    } catch (e) {
      stopped = true;
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      await pollP;
      stopped = true;
      setRefreshing(null);
    }
  };

  const deleteTest = async (t) => {
    if (!window.confirm("Delete this test row from history?")) return;
    try {
      await api.delete(`/woorank/tests/${t.test_id}`);
      setTests((rows) => rows.filter((r) => r.test_id !== t.test_id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  // ── Extended Woorank endpoints (PDF / Metrics / Keywords / CWV) ────
  // Each handler stores its result on `extras[url]` so the panel below
  // the URL list can render whatever was last fetched.
  const [extras, setExtras] = useState({});   // { [url]: { metrics, keywords, cwv } }
  const [extrasBusy, setExtrasBusy] = useState({}); // { [url+kind]: bool }

  // ── Competitors (per-client, unlimited — Woorank itself limits to 3) ──
  const [competitors, setCompetitors] = useState([]);
  const [newCompUrl, setNewCompUrl] = useState("");
  const [newCompLabel, setNewCompLabel] = useState("");
  const [compBusy, setCompBusy] = useState(null); // "add" | comp_id
  const loadCompetitors = useCallback(async () => {
    try {
      const { data } = await api.get(`/woorank/clients/${clientId}/competitors`);
      setCompetitors(data.competitors || []);
    } catch { /* silent */ }
  }, [clientId]);
  useEffect(() => { loadCompetitors(); }, [loadCompetitors]);

  const addCompetitor = async () => {
    if (!newCompUrl.trim()) { toast.error("Enter a URL"); return; }
    setCompBusy("add");
    try {
      const { data } = await api.post(
        `/woorank/clients/${clientId}/competitors`,
        { url: newCompUrl.trim(), label: newCompLabel.trim() || null });
      setCompetitors(data.competitors || []);
      setNewCompUrl(""); setNewCompLabel("");
      toast.success("Competitor added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add competitor");
    } finally { setCompBusy(null); }
  };
  const removeCompetitor = async (c) => {
    if (!window.confirm(`Remove ${c.url} from this client's competitor list?`)) return;
    try {
      const { data } = await api.delete(
        `/woorank/clients/${clientId}/competitors/${c.comp_id}`);
      setCompetitors(data.competitors || []);
      toast.success("Removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const fetchCompetitorReview = async (c) => {
    setCompBusy(c.comp_id);
    try {
      const { data } = await api.post(
        `/woorank/clients/${clientId}/competitors/${c.comp_id}/fetch`);
      setCompetitors(data.competitors || []);
      toast.success(`Fetched · score ${data.test?.score ?? "—"} for ${c.url}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fetch failed");
    } finally { setCompBusy(null); }
  };
  const fetchAllCompetitors = async () => {
    if (competitors.length === 0) return;
    for (const c of competitors) {
      // eslint-disable-next-line no-await-in-loop
      await fetchCompetitorReview(c);
    }
  };

  const setBusyFor = (url, kind, val) =>
    setExtrasBusy((b) => ({ ...b, [`${url}::${kind}`]: val }));
  const isBusyFor = (url, kind) => !!extrasBusy[`${url}::${kind}`];

  const downloadPdf = async (u) => {
    setBusyFor(u.url, "pdf", true);
    try {
      const resp = await api.get(`/woorank/clients/${clientId}/pdf`,
        { params: { url: u.url }, responseType: "blob" });
      // Guess filename from Content-Disposition header if the server set one.
      const cd = resp.headers?.["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : `woorank_${Date.now()}.pdf`;
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success("Woorank PDF downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF download failed");
    } finally { setBusyFor(u.url, "pdf", false); }
  };

  const fetchMetrics = async (u) => {
    setBusyFor(u.url, "metrics", true);
    try {
      const { data } = await api.get(`/woorank/clients/${clientId}/metrics`,
        { params: { url: u.url } });
      setExtras((x) => ({ ...x, [u.url]: { ...(x[u.url] || {}), metrics: data.payload } }));
      toast.success("Metrics fetched");
      setSelectedUrl(u.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Metrics failed");
    } finally { setBusyFor(u.url, "metrics", false); }
  };

  const fetchKeywords = async (u) => {
    setBusyFor(u.url, "keywords", true);
    try {
      const { data } = await api.get(`/woorank/clients/${clientId}/keywords`,
        { params: { url: u.url } });
      setExtras((x) => ({ ...x, [u.url]: { ...(x[u.url] || {}), keywords: data.payload } }));
      toast.success("Keywords fetched");
      setSelectedUrl(u.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Keywords failed");
    } finally { setBusyFor(u.url, "keywords", false); }
  };

  const fetchCwv = async (u) => {
    setBusyFor(u.url, "cwv", true);
    try {
      const { data } = await api.get(`/woorank/clients/${clientId}/cwv`,
        { params: { url: u.url } });
      setExtras((x) => ({ ...x, [u.url]: { ...(x[u.url] || {}), cwv: data.payload } }));
      toast.success("Core Web Vitals fetched");
      setSelectedUrl(u.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "CWV failed");
    } finally { setBusyFor(u.url, "cwv", false); }
  };

  const testsForUrl = useMemo(
    () => tests.filter((t) => t.url === selectedUrl),
    [tests, selectedUrl],
  );

  const latestRun = testsForUrl[0] || null;

  const chartData = useMemo(() => {
    return testsForUrl.slice().reverse().map((r) => ({
      ts: new Date(r.run_at).toLocaleDateString(undefined,
        { day: "numeric", month: "short", year: "2-digit" }),
      score: r.score,
    }));
  }, [testsForUrl]);

  const scoreDelta = useMemo(() => {
    if (testsForUrl.length < 2) return null;
    const a = testsForUrl[0].score;
    const b = testsForUrl[1].score;
    if (a == null || b == null) return null;
    return a - b;
  }, [testsForUrl]);

  const sectionKeys = useMemo(() => {
    if (!latestRun?.section_scores) return [];
    return Object.keys(latestRun.section_scores);
  }, [latestRun]);

  return (
    <div className="space-y-6" data-testid="client-woorank-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-[#00A9E0]/10 grid place-items-center">
            <Radar className="h-5 w-5 text-[#00A9E0]" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight">
              Woorank SEO Review
            </h1>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Per-property Woorank SEO score, section grades + issues.
              A monthly scheduled rollup keeps the trend history flowing.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadAll} disabled={loading}
          className="rounded-sm" data-testid="woorank-reload-btn">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                   : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Reload
        </Button>
      </div>

      {masterState && !masterState.configured && (
        <Card className="rounded-sm border-amber-300 bg-amber-50"
          data-testid="woorank-no-key-warning">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-[12px] text-amber-900 leading-relaxed">
              <strong>Woorank API key not configured.</strong>{" "}
              Fetches will fail until an admin sets the master key on{" "}
              <a href="/integrations/woorank"
                className="underline font-semibold hover:text-amber-950">
                Integrations → Woorank
              </a>.
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <div className="label-overline">Properties to test</div>
            <Badge variant="outline" className="rounded-sm text-[10px] font-mono">
              {urls.length}
            </Badge>
          </div>

          {loading ? (
            <div className="text-center py-6 text-[12px] text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading…
            </div>
          ) : urls.length === 0 ? (
            <div className="text-center py-6 text-[12px] text-zinc-500 border
              border-dashed border-zinc-300 rounded-sm">
              No property URLs yet. Add one below, or set a{" "}
              <em>Website URL</em> on any property in the Properties tab.
            </div>
          ) : (
            <div className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm">
              {urls.map((u) => {
                const hasHistory = tests.some((t) => t.url === u.url);
                const isSelected = selectedUrl === u.url;
                return (
                  <div key={u.url_id}
                    className={`px-3 py-2.5 flex items-center gap-3 hover:bg-zinc-50
                      cursor-pointer transition-colors ${isSelected ? "bg-cyan-50/50" : ""}`}
                    onClick={() => setSelectedUrl(u.url)}
                    data-testid={`woorank-url-row-${u.url_id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <a href={u.url} target="_blank" rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="font-mono text-[12px] text-zinc-800 hover:text-[#00A9E0]
                            truncate max-w-[380px] inline-flex items-center gap-1">
                          {u.url}
                          <ExternalLink className="h-2.5 w-2.5 opacity-50" />
                        </a>
                        <Badge className={`rounded-sm text-[9px] font-mono ${
                          u.source === "business_unit"
                            ? "bg-emerald-100 text-emerald-800"
                            : "bg-sky-100 text-sky-800"}`}>
                          {u.source === "business_unit" ? "PROPERTY" : "CUSTOM"}
                        </Badge>
                        {u.label && (
                          <span className="text-[10px] text-zinc-500 truncate">
                            {u.label}
                          </span>
                        )}
                        {!hasHistory && (
                          <span className="text-[9px] font-mono text-zinc-400 uppercase tracking-wider">
                            No history
                          </span>
                        )}
                      </div>
                    </div>
                    <Button size="sm" variant="outline"
                      onClick={(e) => { e.stopPropagation(); refreshCrawl(u); }}
                      disabled={refreshing === u.url}
                      className="rounded-sm"
                      title="Trigger a new Woorank crawl (async — completes in ~60s)"
                      data-testid={`woorank-refresh-btn-${u.url_id}`}>
                      {refreshing === u.url
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <><RotateCw className="h-3.5 w-3.5 mr-1" /> Refresh crawl</>}
                    </Button>
                    <Button size="sm"
                      onClick={(e) => { e.stopPropagation(); fetchLatest(u); }}
                      disabled={running === u.url}
                      className="rounded-sm bg-[#00A9E0] hover:bg-[#0089B8] text-white"
                      data-testid={`woorank-fetch-btn-${u.url_id}`}>
                      {running === u.url
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <><Play className="h-3.5 w-3.5 mr-1" /> Fetch latest</>}
                    </Button>
                    {u.source === "custom" && (
                      <Button size="sm" variant="ghost"
                        onClick={(e) => { e.stopPropagation(); removeUrl(u); }}
                        className="rounded-sm text-red-600 hover:bg-red-50 p-1.5"
                        data-testid={`woorank-remove-url-${u.url_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ─── Extended endpoint actions for the selected URL ────────── */}
          {selectedUrl && (
            <div className="pt-3 border-t border-zinc-100">
              <div className="label-overline mb-2">
                Extended endpoints · acts on <span className="font-mono normal-case text-zinc-700">{selectedUrl}</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline"
                  onClick={() => downloadPdf({ url: selectedUrl })}
                  disabled={isBusyFor(selectedUrl, "pdf")}
                  className="rounded-sm"
                  title="Download the white-label Woorank PDF report"
                  data-testid="woorank-pdf-btn">
                  {isBusyFor(selectedUrl, "pdf")
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                    : <FileText className="h-3.5 w-3.5 mr-1" />}
                  Download PDF
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => fetchMetrics({ url: selectedUrl })}
                  disabled={isBusyFor(selectedUrl, "metrics")}
                  className="rounded-sm"
                  title="Fetch Woorank's pre-computed scoring history"
                  data-testid="woorank-metrics-btn">
                  {isBusyFor(selectedUrl, "metrics")
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                    : <BarChart3 className="h-3.5 w-3.5 mr-1" />}
                  Fetch metrics
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => fetchKeywords({ url: selectedUrl })}
                  disabled={isBusyFor(selectedUrl, "keywords")}
                  className="rounded-sm"
                  title="Fetch Woorank keyword ranking data"
                  data-testid="woorank-keywords-btn">
                  {isBusyFor(selectedUrl, "keywords")
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                    : <KeyRound className="h-3.5 w-3.5 mr-1" />}
                  Fetch keywords
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => fetchCwv({ url: selectedUrl })}
                  disabled={isBusyFor(selectedUrl, "cwv")}
                  className="rounded-sm"
                  title="Fetch Core Web Vitals with histograms + INP"
                  data-testid="woorank-cwv-btn">
                  {isBusyFor(selectedUrl, "cwv")
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                    : <Gauge className="h-3.5 w-3.5 mr-1" />}
                  Fetch CWV
                </Button>
              </div>
              {extras[selectedUrl] && (
                <div className="mt-3 space-y-2">
                  {["metrics", "keywords", "cwv"].map((kind) => {
                    const val = extras[selectedUrl][kind];
                    if (!val) return null;
                    return (
                      <details key={kind}
                        className="border border-zinc-200 rounded-sm bg-zinc-50"
                        data-testid={`woorank-extras-${kind}`}>
                        <summary className="cursor-pointer px-3 py-2 flex items-center gap-2 text-[12px] font-semibold text-zinc-700 hover:bg-zinc-100">
                          <ChevronDown className="h-3.5 w-3.5" />
                          {kind === "metrics" && "Scoring history (GET /metrics)"}
                          {kind === "keywords" && "Keyword tracking (GET /keywords)"}
                          {kind === "cwv" && "Core Web Vitals (GET /cwv)"}
                        </summary>
                        <pre className="text-[10px] font-mono px-3 pb-3 pt-1
                          max-h-72 overflow-y-auto whitespace-pre-wrap">
                          {JSON.stringify(val, null, 2)}
                        </pre>
                      </details>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="pt-3 border-t border-zinc-100">
            <div className="label-overline mb-2">Add another URL to test</div>
            <div className="flex gap-2 flex-wrap">
              <Input value={newUrl} onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://example.com/pricing"
                className="rounded-sm font-mono text-[12px] flex-1 min-w-[260px]"
                data-testid="woorank-new-url-input" />
              <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Optional label"
                className="rounded-sm text-[12px] w-40"
                data-testid="woorank-new-url-label" />
              <Button onClick={addUrl} disabled={adding || !newUrl.trim()}
                className="rounded-sm bg-[#00A9E0] hover:bg-[#0089B8] text-white"
                data-testid="woorank-add-url-btn">
                {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <><Plus className="h-3.5 w-3.5 mr-1" /> Add</>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedUrl && (
        <>
          <Card className="rounded-sm border-zinc-200">
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div className="min-w-0">
                  <div className="label-overline">Selected URL</div>
                  <div className="font-mono text-[12px] text-zinc-800 truncate max-w-[500px]">
                    {selectedUrl}
                  </div>
                </div>
                {latestRun && (
                  <div className="text-[10px] font-mono text-zinc-500 text-right">
                    Latest: {new Date(latestRun.run_at).toLocaleString()}
                    {latestRun.source === "scheduled" && (
                      <Badge className="ml-2 rounded-sm text-[8px] font-mono bg-zinc-200 text-zinc-700">
                        SCHEDULED
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              {latestRun ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Big score circle */}
                  <div className="rounded-sm border border-zinc-200 p-5 text-center
                    md:col-span-1 flex flex-col items-center justify-center"
                    data-testid="woorank-score-card">
                    <div className="text-[9px] uppercase tracking-wider font-mono text-zinc-500 mb-2">
                      Woorank score
                    </div>
                    <div className="flex items-center justify-center h-28 w-28
                      rounded-full font-display font-bold text-4xl text-white"
                      style={{ backgroundColor: scoreColor(latestRun.score) }}>
                      {latestRun.score ?? "—"}
                    </div>
                    <div className="text-[11px] text-zinc-700 font-semibold mt-2">
                      {scoreLabel(latestRun.score)}
                    </div>
                    {scoreDelta != null && (
                      <div className={`text-[11px] font-mono mt-1 ${
                        scoreDelta > 0 ? "text-emerald-700"
                        : scoreDelta < 0 ? "text-red-700" : "text-zinc-500"}`}>
                        {scoreDelta > 0 && "▲ "}{scoreDelta < 0 && "▼ "}
                        {scoreDelta === 0 ? "no change from last run"
                          : `${scoreDelta > 0 ? "+" : ""}${scoreDelta} vs last run`}
                      </div>
                    )}
                  </div>

                  {/* Section scores + issue count summary */}
                  <div className="md:col-span-2 space-y-4">
                    {sectionKeys.length > 0 && (
                      <div>
                        <div className="label-overline mb-2">Section scores</div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {sectionKeys.map((k) => {
                            const s = latestRun.section_scores?.[k];
                            const label = k.replace(/_/g, " ");
                            return (
                              <div key={k} className="rounded-sm bg-zinc-50 p-2.5">
                                <div className="text-[9px] uppercase tracking-wider font-mono text-zinc-500 truncate">
                                  {label}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="inline-block h-6 w-6 rounded-full text-white
                                    font-mono font-bold text-[10px] leading-6 text-center"
                                    style={{ backgroundColor: scoreColor(s) }}>
                                    {s ?? "—"}
                                  </span>
                                  <span className="text-[10px] text-zinc-600 font-semibold">
                                    {scoreLabel(s)}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {latestRun.issue_counts && (
                      <div className="flex items-center gap-3 flex-wrap">
                        <Badge className="rounded-sm text-[10px] font-mono bg-emerald-100 text-emerald-800">
                          {latestRun.issue_counts.passed || 0} passed
                        </Badge>
                        <Badge className="rounded-sm text-[10px] font-mono bg-red-100 text-red-800">
                          {latestRun.issue_counts.failed || 0} failing
                        </Badge>
                        <Badge variant="outline" className="rounded-sm text-[10px] font-mono">
                          {latestRun.issue_counts.total_criteria || 0} total checks
                        </Badge>
                      </div>
                    )}

                    {latestRun.traffic && (latestRun.traffic.monthly_visits != null) && (
                      <div className="rounded-sm bg-zinc-50 p-3">
                        <div className="label-overline mb-1">Traffic estimate</div>
                        <div className="text-[12px] text-zinc-700">
                          <strong className="font-mono">
                            {latestRun.traffic.monthly_visits?.toLocaleString?.() ?? "—"}
                          </strong>{" "}
                          monthly visits
                          {latestRun.traffic.monthly_trend && (
                            <span className="ml-2 text-zinc-500 font-mono">
                              trend: {latestRun.traffic.monthly_trend}
                            </span>
                          )}
                        </div>
                        {latestRun.traffic.top_sources?.length > 0 && (
                          <div className="text-[10px] text-zinc-500 font-mono mt-1">
                            Top sources: {latestRun.traffic.top_sources.join(", ")}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-[12px] text-zinc-500 border border-dashed border-zinc-300 rounded-sm">
                  <Info className="h-4 w-4 inline mr-1" />
                  No Woorank data yet for this URL — click{" "}
                  <strong>Fetch latest</strong> above (or{" "}
                  <strong>Refresh crawl</strong> if Woorank has never crawled it).
                </div>
              )}
            </CardContent>
          </Card>

          {chartData.length > 0 && (
            <Card className="rounded-sm border-zinc-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="h-4 w-4 text-zinc-500" />
                  <div className="label-overline">
                    Woorank score trend · {chartData.length} data-point(s)
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={chartData}
                    margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                    <CartesianGrid stroke="#e4e4e7" strokeDasharray="3 3" />
                    <XAxis dataKey="ts" tick={{ fontSize: 10, fill: "#71717a" }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#71717a" }} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 2 }} />
                    <Line type="monotone" dataKey="score"
                      stroke="#00A9E0" strokeWidth={2.5} dot={{ r: 4 }}
                      name="Score" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {latestRun && (latestRun.issues || []).length > 0 && (
            <Card className="rounded-sm border-zinc-200">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="h-4 w-4 text-amber-600" />
                  <div className="label-overline">
                    Top SEO issues to fix ({latestRun.issues.length})
                  </div>
                </div>
                <div className="divide-y divide-zinc-100">
                  {latestRun.issues.map((o, i) => (
                    <div key={o.name || i} className="py-2.5"
                      data-testid={`woorank-issue-${i}`}>
                      <div className="flex items-start gap-3">
                        <Badge className={`rounded-sm text-[9px] font-mono uppercase ${statusColor(o.status)}`}>
                          {o.status}
                        </Badge>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-[13px] text-zinc-900">
                              {(o.name || "").replace(/_/g, " ")}
                            </span>
                            {o.category && (
                              <Badge variant="outline" className="rounded-sm text-[9px] font-mono">
                                {o.category}
                              </Badge>
                            )}
                            {o.impact && (
                              <Badge className="rounded-sm text-[9px] font-mono bg-zinc-100 text-zinc-700">
                                impact: {o.impact}
                              </Badge>
                            )}
                          </div>
                          {o.value != null && typeof o.value !== "object" && (
                            <p className="text-[11px] text-zinc-600 mt-1 leading-relaxed font-mono">
                              {String(o.value).slice(0, 200)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {testsForUrl.length > 0 && (
            <Card className="rounded-sm border-zinc-200">
              <CardContent className="p-5">
                <div className="label-overline mb-3">
                  Full history · {testsForUrl.length} row(s)
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-left border-b border-zinc-200">
                        <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">When</th>
                        <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider text-center">Score</th>
                        <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider text-center">Passed</th>
                        <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider text-center">Failing</th>
                        <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Source</th>
                        <th className="py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {testsForUrl.map((t) => (
                        <tr key={t.test_id} className="border-b border-zinc-100 hover:bg-zinc-50"
                          data-testid={`woorank-history-row-${t.test_id}`}>
                          <td className="py-1.5 font-mono text-zinc-700">
                            {new Date(t.run_at).toLocaleString(undefined,
                              { day: "numeric", month: "short", year: "2-digit",
                                hour: "2-digit", minute: "2-digit" })}
                          </td>
                          <td className="py-1.5 text-center">
                            <span className="inline-block h-7 w-7 rounded-full text-white
                              font-mono font-bold text-[11px] leading-7"
                              style={{ backgroundColor: scoreColor(t.score) }}>
                              {t.score ?? "—"}
                            </span>
                          </td>
                          <td className="py-1.5 text-center font-mono text-emerald-700">
                            {t.issue_counts?.passed ?? "—"}
                          </td>
                          <td className="py-1.5 text-center font-mono text-red-700">
                            {t.issue_counts?.failed ?? "—"}
                          </td>
                          <td className="py-1.5 font-mono text-zinc-500">
                            {t.source}
                          </td>
                          <td className="py-1.5 text-right">
                            <button onClick={() => deleteTest(t)}
                              className="text-red-600 hover:bg-red-50 rounded-sm p-1"
                              data-testid={`woorank-delete-test-${t.test_id}`}>
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ═══ Competitors (client-level, unlimited) ═══════════════════════ */}
      <Card className="rounded-sm border-zinc-200 overflow-hidden"
        data-testid="woorank-competitors-card">
        <div className="bg-gradient-to-r from-fuchsia-50 to-transparent
          px-5 py-4 border-b border-zinc-100 flex items-center gap-3 flex-wrap">
          <Radar className="h-5 w-5 text-fuchsia-700" />
          <div className="flex-1">
            <h2 className="font-display font-bold text-lg text-zinc-900">
              Competitors
            </h2>
            <p className="text-[11px] text-zinc-500">
              Track competitor SEO scores side-by-side — no 3-competitor
              limit like the Woorank dashboard.
            </p>
          </div>
          <Badge variant="outline" className="rounded-sm text-[10px] font-mono">
            {competitors.length}
          </Badge>
          {competitors.length > 0 && (
            <Button size="sm" onClick={fetchAllCompetitors}
              disabled={compBusy !== null}
              className="rounded-sm bg-fuchsia-700 hover:bg-fuchsia-800 text-white"
              data-testid="woorank-fetch-all-competitors-btn">
              {compBusy !== null && compBusy !== "add"
                ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                : <Play className="h-3.5 w-3.5 mr-1" />}
              Fetch all
            </Button>
          )}
        </div>
        <CardContent className="p-5 space-y-3">
          {/* Side-by-side comparison chart — client's best score vs.
              each competitor's latest score. Uses the freshest run of
              the currently-selected client URL, or the highest scoring
              URL if none is selected. */}
          {competitors.some((c) => c.latest_score != null) && (() => {
            const clientLatest = testsForUrl.find((t) => t.source !== "competitor") || null;
            const rows = [
              {
                name: (clientLatest?.url || selectedUrl || "This client")
                  .replace(/^https?:\/\//, "").replace(/\/$/, "").slice(0, 28),
                score: clientLatest?.score ?? null,
                color: "#00A9E0",
                is_client: true,
              },
              ...competitors
                .filter((c) => c.latest_score != null)
                .map((c) => ({
                  name: c.url.replace(/^https?:\/\//, "").replace(/\/$/, "").slice(0, 28),
                  score: c.latest_score,
                  color: scoreColor(c.latest_score),
                  label: c.label,
                })),
            ].filter((r) => r.score != null);
            if (rows.length < 2) return null;
            // Sort descending so leader is on the left.
            rows.sort((a, b) => (b.score || 0) - (a.score || 0));
            return (
              <div className="rounded-sm bg-zinc-50 p-3 mb-2"
                data-testid="woorank-comparison-chart">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-3.5 w-3.5 text-fuchsia-700" />
                  <div className="label-overline">
                    Side-by-side · {rows.length} sites ranked by Woorank score
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={Math.max(200, rows.length * 34)}>
                  <BarChart data={rows} layout="vertical"
                    margin={{ top: 8, right: 40, left: 0, bottom: 8 }}>
                    <CartesianGrid stroke="#e4e4e7" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]}
                      tick={{ fontSize: 10, fill: "#71717a" }} />
                    <YAxis type="category" dataKey="name" width={220}
                      tick={{ fontSize: 10, fill: "#3f3f46" }}
                      tickFormatter={(v) => v} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 2 }} />
                    <Bar dataKey="score" radius={[0, 3, 3, 0]}>
                      {rows.map((r, i) => (
                        <Cell key={i}
                          fill={r.color}
                          stroke={r.is_client ? "#0b7bb1" : undefined}
                          strokeWidth={r.is_client ? 2 : 0} />
                      ))}
                      <LabelList dataKey="score" position="right"
                        style={{ fontSize: 11, fontWeight: 700, fill: "#3f3f46" }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="text-[10px] font-mono text-zinc-500 mt-1 leading-relaxed">
                  Blue-bordered bar is this client. Colours are the same
                  score-tier palette used in the score circles.
                </div>
              </div>
            );
          })()}
          {competitors.length === 0 ? (
            <div className="text-center py-6 text-[12px] text-zinc-500 border
              border-dashed border-zinc-300 rounded-sm">
              No competitors registered yet. Add competitor URLs below and
              hit <strong>Fetch</strong> to see their Woorank SEO scores
              alongside this client&apos;s.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-left border-b border-zinc-200">
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider text-center">Score</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Competitor URL</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Label</th>
                    <th className="py-1.5 font-mono uppercase text-[9px] text-zinc-500 tracking-wider">Last fetched</th>
                    <th className="py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {competitors.map((c) => (
                    <tr key={c.comp_id} className="border-b border-zinc-100 hover:bg-zinc-50"
                      data-testid={`woorank-competitor-row-${c.comp_id}`}>
                      <td className="py-1.5 text-center">
                        <span className="inline-block h-7 w-7 rounded-full text-white
                          font-mono font-bold text-[11px] leading-7"
                          style={{ backgroundColor: scoreColor(c.latest_score) }}>
                          {c.latest_score ?? "—"}
                        </span>
                      </td>
                      <td className="py-1.5">
                        <a href={c.url} target="_blank" rel="noreferrer"
                          className="font-mono text-[11px] text-zinc-800 hover:text-fuchsia-700
                            inline-flex items-center gap-1">
                          {c.url}
                          <ExternalLink className="h-2.5 w-2.5 opacity-50" />
                        </a>
                      </td>
                      <td className="py-1.5 text-zinc-600">{c.label || "—"}</td>
                      <td className="py-1.5 font-mono text-zinc-500">
                        {c.latest_run_at
                          ? new Date(c.latest_run_at).toLocaleString(undefined,
                              { day: "numeric", month: "short", year: "2-digit" })
                          : "never"}
                      </td>
                      <td className="py-1.5 text-right space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="outline"
                          onClick={() => fetchCompetitorReview(c)}
                          disabled={compBusy === c.comp_id}
                          className="rounded-sm h-6 px-2 text-[10px]"
                          data-testid={`woorank-fetch-competitor-${c.comp_id}`}>
                          {compBusy === c.comp_id
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : <><Play className="h-3 w-3 mr-1" /> Fetch</>}
                        </Button>
                        <button onClick={() => removeCompetitor(c)}
                          className="text-red-600 hover:bg-red-50 rounded-sm p-1"
                          data-testid={`woorank-remove-competitor-${c.comp_id}`}>
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Add-competitor row */}
          <div className="pt-3 border-t border-zinc-100">
            <div className="label-overline mb-2">Add a competitor URL</div>
            <div className="flex gap-2 flex-wrap">
              <Input value={newCompUrl}
                onChange={(e) => setNewCompUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && compBusy !== "add") addCompetitor(); }}
                placeholder="https://competitor.com"
                className="rounded-sm font-mono text-[12px] flex-1 min-w-[220px]"
                data-testid="woorank-new-competitor-url" />
              <Input value={newCompLabel}
                onChange={(e) => setNewCompLabel(e.target.value)}
                placeholder="Optional label (e.g. Direct competitor)"
                className="rounded-sm text-[12px] w-56"
                data-testid="woorank-new-competitor-label" />
              <Button onClick={addCompetitor}
                disabled={compBusy === "add" || !newCompUrl.trim()}
                className="rounded-sm bg-fuchsia-700 hover:bg-fuchsia-800 text-white"
                data-testid="woorank-add-competitor-btn">
                {compBusy === "add"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <><Plus className="h-3.5 w-3.5 mr-1" /> Add</>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
